import { groupsOf, poolOf, tierAmount, tiersOf } from '../../ServiceDashboard/components/serviceCatalog';

export function norm(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[.,;:]+$/g, '');
}

function contentKey(row) {
  if (row?.section === 'serviceTerms') {
    const title = norm(row.title);
    const body = norm(row.body);
    if (!title && !body) return '';
    return `serviceTerms:${title}|${body}`;
  }
  const text = norm(row?.text);
  if (!text) return '';
  return `${row.section}:${text}`;
}

function termText(row) {
  if (row?.section === 'serviceTerms') return norm(row.body);
  return norm(row?.text);
}

function covers(longer, shorter) {
  if (!shorter) return true;
  if (!longer) return false;
  return longer === shorter || longer.includes(shorter);
}

function sameOrContained(left, right) {
  if (!left || !right || left.section !== right.section) return false;
  if (left.section === 'serviceTerms') {
    const leftTitle = norm(left.title);
    const rightTitle = norm(right.title);
    const leftBody = termText(left);
    const rightBody = termText(right);
    if (!leftTitle && !rightTitle && !leftBody && !rightBody) return false;
    const sameTitle = Boolean(leftTitle) && leftTitle === rightTitle;
    if (sameTitle && covers(leftBody, rightBody)) return true;
    if (sameTitle && covers(rightBody, leftBody)) return true;
    if (leftBody && rightBody && (covers(leftBody, rightBody) || covers(rightBody, leftBody))) return true;
    return false;
  }
  const leftText = termText(left);
  const rightText = termText(right);
  if (!leftText || !rightText) return false;
  return covers(leftText, rightText) || covers(rightText, leftText);
}

function preferRow(current, incoming) {
  const sources = [...(current.sources || [])];
  (incoming.sources || []).forEach((source) => {
    if (source?.catalogId && !sources.some((item) => item.catalogId === source.catalogId)) {
      sources.push(source);
    }
  });
  const currentText = termText(current);
  const incomingText = termText(incoming);
  const richer = incomingText.length > currentText.length ? incoming : current;
  return {
    ...current,
    ...richer,
    key: current.key || richer.key,
    selected: current.selected !== false || incoming.selected !== false,
    custom: Boolean(current.custom && incoming.custom),
    sources,
  };
}

function absorbSimilar(rows) {
  const kept = [];
  (rows || []).forEach((row) => {
    const index = kept.findIndex((item) => sameOrContained(item, row));
    if (index < 0) {
      kept.push(row);
      return;
    }
    kept[index] = preferRow(kept[index], row);
  });
  return kept;
}

export function catalogTermsOf(service) {
  const raw = service?.catalog?.terms;
  const rows = (value) => (Array.isArray(value) ? value : []);
  if (!raw || typeof raw !== 'object') {
    return { serviceTerms: [], responsibilities: [], exclusions: [], notes: [] };
  }
  return {
    serviceTerms: rows(raw.serviceTerms),
    responsibilities: rows(raw.responsibilities),
    exclusions: rows(raw.exclusions),
    notes: rows(raw.notes),
  };
}

export function termRowsFromCatalog(service) {
  const terms = catalogTermsOf(service);
  const name = String(service?.service || service?.name || 'Service').trim();
  const catalogId = String(service?.id || '');
  const source = { catalogId, name };
  const rows = [];
  terms.serviceTerms.forEach((row) => {
    const title = String(row?.title || '').trim();
    const body = String(row?.body || '').trim();
    if (!title && !body) return;
    rows.push({
      key: `serviceTerms:${norm(title)}|${norm(body)}`,
      section: 'serviceTerms',
      title,
      body,
      text: '',
      sources: [source],
    });
  });
  ['responsibilities', 'exclusions', 'notes'].forEach((section) => {
    terms[section].forEach((row) => {
      const text = String(row?.text || '').trim();
      if (!text) return;
      rows.push({
        key: `${section}:${norm(text)}`,
        section,
        title: '',
        body: '',
        text,
        sources: [source],
      });
    });
  });
  return rows;
}

export function mergeTermRows(current, incoming) {
  const map = new Map();
  const put = (row) => {
    if (!row) return;
    const signature = contentKey(row);
    const slot = signature || row.key;
    if (!slot) return;
    const prev = map.get(slot);
    if (!prev) {
      map.set(slot, {
        ...row,
        selected: row.selected !== false,
        sources: [...(row.sources || [])],
      });
      return;
    }
    const sources = [...(prev.sources || [])];
    (row.sources || []).forEach((source) => {
      if (source?.catalogId && !sources.some((item) => item.catalogId === source.catalogId)) {
        sources.push(source);
      }
    });
    map.set(slot, {
      ...prev,
      selected: prev.selected !== false || row.selected !== false,
      custom: Boolean(prev.custom && row.custom),
      sources,
    });
  };
  (current || []).forEach(put);
  (incoming || []).forEach(put);
  return absorbSimilar([...map.values()]);
}

export function dropServiceTerms(rows, catalogId) {
  return (rows || []).flatMap((row) => {
    if (row.custom) return [row];
    const sources = (row.sources || []).filter((item) => item.catalogId !== catalogId);
    if (!sources.length) return [];
    return [{ ...row, sources }];
  });
}

export function rowsFromClientTerms(text) {
  const raw = Array.isArray(text) ? text.join('\n') : String(text || '');
  return raw
    .split(/\n+/)
    .map((line) => line.replace(/^[-•]\s*/, '').trim())
    .filter(Boolean)
    .map((line, index) => ({
      key: `custom:${index}:${norm(line)}`,
      section: 'serviceTerms',
      title: '',
      body: line,
      text: '',
      selected: true,
      custom: true,
      sources: [],
    }));
}

export function termsToClientText(rows) {
  return (rows || [])
    .filter((row) => row.selected)
    .map((row) => {
      if (row.section === 'serviceTerms') {
        return [row.title, row.body].map((part) => String(part || '').trim()).filter(Boolean).join(': ');
      }
      return String(row.text || '').trim();
    })
    .filter(Boolean)
    .join('\n');
}

export function scopeGroups(service, tier) {
  if (tier) return groupsOf(tier);
  return poolOf(service);
}

/** Tier price wins. Otherwise the ticked tasks (or their group price) are the line price. */
export function selectionPrice(tier, groups, checked) {
  if (tier && tierAmount(tier) > 0) return tierAmount(tier);
  return (groups || []).reduce((sum, group) => {
    const tasks = (group.tasks || []).filter((task) => checked[task.id]);
    if (!tasks.length) return sum;
    const taskSum = tasks.reduce(
      (inner, task) => inner + (Number(task.price) || 0) * (Number(task.quantity) || 1),
      0,
    );
    if (taskSum > 0) return sum + taskSum;
    return sum + (Number(group.price) || 0);
  }, 0);
}

/** Scope and instructions from every saved copy of a catalogue task. */
export function catalogTaskIndex(service) {
  const byId = new Map();
  const byTitle = new Map();
  const merge = (map, key, info) => {
    if (!key) return;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, info);
      return;
    }
    map.set(key, {
      groupName: prev.groupName || info.groupName,
      scope: prev.scope || info.scope,
      instructions: prev.instructions || info.instructions,
    });
  };
  [...poolOf(service), ...tiersOf(service).flatMap((tier) => groupsOf(tier))].forEach((group) => {
    (group.tasks || []).forEach((task) => {
      const info = {
        groupName: String(group?.name || '').trim(),
        scope: String(task?.scope || '').trim(),
        instructions: String(task?.instructions || '').trim(),
      };
      merge(byId, task?.id ? String(task.id) : '', info);
      merge(byTitle, String(task?.name || '').trim().toLowerCase(), info);
    });
  });
  return { byId, byTitle };
}

export function checkedTasks(groups, checked, service) {
  const library = service ? catalogTaskIndex(service) : null;
  return (groups || []).flatMap((group) =>
    (group.tasks || [])
      .filter((task) => checked[task.id])
      .map((task) => {
        const saved = library?.byId.get(String(task.id || ''))
          || library?.byTitle.get(String(task.name || '').trim().toLowerCase());
        return {
          id: task.id,
          title: String(task.name || '').trim(),
          scope: saved?.scope || String(task.scope || '').trim(),
          instructions: saved?.instructions || String(task.instructions || '').trim(),
          requirement: task.includedByDefault === false ? 'OPTIONAL' : 'REQUIRED',
          assignedRole: 'CLEANER',
          frequency: 'EVERY_VISIT',
          price: (Number(task.price) || 0) * (Number(task.quantity) || 1),
          discount: '0',
          groupName: group.name || saved?.groupName || '',
        };
      })
      .filter((task) => task.title || task.scope || task.instructions),
  );
}

function fillQuoteTasks(tasks, lookup) {
  if (!lookup || !Array.isArray(tasks)) return tasks;
  return tasks.map((task) => {
    if (!task || task.quoteOnly) return task;
    const byId = lookup.byId.get(String(task.id || ''));
    const title = String(task.title || task.taskTitle || task.name || '').trim().toLowerCase();
    const match = byId || lookup.byTitle.get(title);
    if (!match) return task;
    const scope = String(task.scope || '').trim() || match.scope;
    const instructions = String(task.instructions || '').trim() || match.instructions;
    const currentGroup = task.groupName == null ? '' : String(task.groupName);
    const groupName = !currentGroup || currentGroup === 'Tasks'
      ? (match.groupName || currentGroup)
      : currentGroup;
    return { ...task, scope, instructions, groupName };
  });
}

function lookupForLine(line, lookups) {
  const keys = [
    line?.catalogServiceId,
    line?.service?.id,
    line?.catalogCode,
    line?.code,
    line?.catalogName,
    line?.name,
    line?.service?.service,
    line?.serviceName,
  ];
  for (const key of keys) {
    const text = String(key || '').trim();
    if (!text) continue;
    const hit = lookups.get(text) || lookups.get(text.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

function fillQuoteLines(lines, lookups) {
  if (!Array.isArray(lines)) return lines;
  return lines.map((line) => {
    const lookup = lookupForLine(line, lookups);
    if (!lookup || !Array.isArray(line?.tasks)) return line;
    return { ...line, tasks: fillQuoteTasks(line.tasks, lookup) };
  });
}

/** Copy catalogue scope and instructions onto quote tasks that do not already have them. */
export function quoteWithCatalogTasks(quote, services) {
  if (!quote || !Array.isArray(services) || !services.length) return quote;
  const lookups = new Map();
  services.forEach((service) => {
    const lookup = catalogTaskIndex(service);
    [service?.id, service?.serviceId, service?.service, service?.name].forEach((key) => {
      const text = String(key || '').trim();
      if (!text) return;
      lookups.set(text, lookup);
      lookups.set(text.toLowerCase(), lookup);
    });
  });
  const apply = (raw) => {
    const workflow = raw?.workflow && typeof raw.workflow === 'object' ? raw.workflow : {};
    const block = raw?.services && typeof raw.services === 'object' ? raw.services : {};
    return {
      ...raw,
      workflow: {
        ...workflow,
        serviceLines: fillQuoteLines(workflow.serviceLines, lookups),
      },
      services: {
        ...block,
        groups: fillQuoteLines(block.groups, lookups),
        data: fillQuoteLines(block.data, lookups),
      },
    };
  };
  if (quote.rawData && typeof quote.rawData === 'object') {
    return { ...quote, rawData: apply(quote.rawData) };
  }
  if (quote.workflow || quote.services) return apply(quote);
  return quote;
}

export { groupsOf, poolOf, tierAmount, tiersOf };
